import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import {
  useBotStatus,
  useDisconnectBot,
  useSelfConnectBot,
  useUpdateBotModel,
} from '../../hooks/useBotConnection';
import { useSlackSearchStatus, useDisconnectSlackSearch } from '../../hooks/useSlackSearch';
import type { PatStatus, SelfConnectResult } from '../../services/api/botConnection';
import { slackSearchConnectUrl } from '../../services/api/slackSearch';

/**
 * Phase 109 (Plan 109-08 / SLACK-SEARCH-03) — outcome notices rendered from
 * the `?slackSearch=` redirect-back parameter the callback (Plan 109-06)
 * sets. `declined` is deliberately NOT an error: a monk who clicked Cancel
 * on Slack's consent screen made a choice, and calling it a failure would
 * be false.
 */
const SLACK_SEARCH_OUTCOME_COPY: Record<'connected' | 'declined' | 'session-expired', string> = {
  connected:
    'Slack search connected. Monka can now search your channels, DMs and group DMs on your behalf.',
  declined: 'Slack search was not connected — the connection was cancelled.',
  'session-expired': 'Your sign-in expired while connecting Slack search. Click Connect to try again.',
};

/**
 * Phase 109 (Plan 109-08 / D-01) — human sentences for the stable `reason`
 * codes plans 109-04/109-06 define on the callback's `?slackSearch=error`
 * redirect. Branch on the CODE only, never on message text (T-109-08-01) —
 * an unrecognised code falls through to a generic sentence rather than
 * rendering the raw value.
 */
const SLACK_SEARCH_ERROR_REASONS: Record<string, string> = {
  SLACK_SEARCH_NOT_CONFIGURED: 'Slack search is not set up on this Monksheets instance yet.',
  SLACK_SEARCH_SCOPES_FORBIDDEN:
    "Slack granted more access than Monka is allowed to use, so nothing was saved. Ask an admin to check the Slack app's configuration.",
  SLACK_SEARCH_SCOPES_INSUFFICIENT:
    'Slack did not grant the access Monka needs to search your channels. Try connecting again.',
  SLACK_IDENTITY_MISMATCH: 'The Slack account you authorized does not match the one linked to your Monk connection.',
  MONK_NOT_CONNECTED: 'Connect Monk in Slack first, then try connecting Slack search again.',
  SLACK_OAUTH_EXCHANGE_FAILED: 'Slack could not complete the connection. Please try again.',
  SLACK_UNAVAILABLE: 'Slack could not be reached. Please try again in a moment.',
  missing_params: 'The connection could not be completed. Please try again.',
};

const SLACK_SEARCH_GENERIC_ERROR = 'Something went wrong connecting Slack search. Please try again.';

/**
 * WR-05: a plain `Record<string, string>` indexed with a fully
 * attacker-controlled URL parameter is unsafe — `reason=__proto__`
 * resolves to `Object.prototype` (a truthy object), which React refuses to
 * render ("Objects are not valid as a React child"), taking down the whole
 * profile route; `reason=constructor`/`toString` resolve to functions and
 * render an empty banner. `Object.hasOwn` guards against every JS-object
 * built-in property name, not just `__proto__`, so this is a general fix
 * rather than a single-case patch.
 */
function resolveSlackSearchErrorReason(reason: string | null): string {
  if (reason && Object.hasOwn(SLACK_SEARCH_ERROR_REASONS, reason)) {
    return SLACK_SEARCH_ERROR_REASONS[reason]!;
  }
  return SLACK_SEARCH_GENERIC_ERROR;
}

/**
 * D-111-10 — mirrors `SLACK_SEARCH_USER_SCOPES` in
 * `backend/src/services/slackOAuthService.ts`. The frontend cannot import
 * from the backend, so this is a deliberate duplicate, not an oversight —
 * keep it in step with the backend constant if that list ever changes.
 */
const SLACK_SEARCH_REQUIRED_SCOPES = [
  'search:read.public',
  'search:read.private',
  'search:read.im',
  'search:read.mpim',
] as const;

/**
 * D-111-10 — a connected-but-narrow monk (a pre-Phase-111 two-scope grant)
 * is told to reconnect here, before their next search hits Plan 111-03's
 * server-side `SLACK_SEARCH_SCOPES_INSUFFICIENT` refusal. This is a
 * usability control layered on that server-side guarantee, not a substitute
 * for it — the pre-check in `getSlackUserTokenForUser` is what actually
 * enforces the requirement.
 *
 * A subset check, not an equality check: extra scopes beyond the four must
 * not trigger the notice. `getSlackSearchStatus` splits the stored scope
 * string on commas and trims but does not fold case, so this folds case
 * itself before comparing.
 */
function hasAllRequiredScopesForReconnectCheck(scopes: readonly string[]): boolean {
  const normalized = scopes.map((s) => s.trim().toLowerCase());
  return SLACK_SEARCH_REQUIRED_SCOPES.every((required) => normalized.includes(required));
}

/** Allowlisted Monka models (mirrors BOT_MODEL_ALLOWLIST, Plan 77-02). "Default" maps to `null` (D-3). */
const MODEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'default (Sonnet)' },
  { value: 'sonnet', label: 'sonnet' },
  { value: 'opus', label: 'opus' },
  { value: 'haiku', label: 'haiku' },
  { value: 'fable', label: 'fable' },
];

function PatStatusBadge({ status }: { status: PatStatus }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        Active
      </span>
    );
  }
  const label = status === 'expired' ? 'Expired' : 'Revoked';
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      {label}
    </span>
  );
}

/**
 * "Monk (Slack bot)" ProfilePage section (D-13, IDENT-04) — mounted beside
 * `PersonalApiTokensSection`. Shows connection status/connected-since/PAT
 * status when connected, and offers a password-style Claude setup token
 * paste form shared by both states (quick-260817-hpp): "Connect" when
 * never-connected, "Replace token" when already connected. Never renders
 * any token plaintext.
 */
export function MonkBotSection() {
  const { data: status, isLoading } = useBotStatus();
  const disconnectBot = useDisconnectBot();
  const selfConnect = useSelfConnectBot();
  const updateModel = useUpdateBotModel();

  const {
    data: slackSearchStatus,
    isLoading: isSlackSearchLoading,
    refetch: refetchSlackSearchStatus,
  } = useSlackSearchStatus();
  const disconnectSlackSearch = useDisconnectSlackSearch();

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [warnMessage, setWarnMessage] = useState<string | null>(null);

  const [showSlackSearchDisconnectConfirm, setShowSlackSearchDisconnectConfirm] = useState(false);
  const [slackSearchRevokeCaveat, setSlackSearchRevokeCaveat] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 109 (Plan 109-08) — the redirect-back outcome is captured ONCE
  // from `useSearchParams()` via a `useState` initializer, not read live on
  // every render. If the rendered banner were driven directly by
  // `searchParams.get(...)`, clearing the parameter below would make the
  // banner disappear in the same render pass it was meant to be shown in —
  // the user would never see it. Capturing it locally lets the banner
  // persist for this page view while the URL itself is cleared so a
  // refresh does not replay a stale outcome (T-109-08-08). The initializer
  // function form runs only on mount, so this still reads the URL
  // `useSearchParams()` resolved on first render.
  const [slackSearchOutcome] = useState(() => searchParams.get('slackSearch'));
  const [slackSearchErrorReason] = useState(() => searchParams.get('reason'));

  useEffect(() => {
    if (!slackSearchOutcome) return;
    if (slackSearchOutcome === 'connected') {
      refetchSlackSearchStatus();
    }
    setSearchParams(
      (prev) => {
        prev.delete('slackSearch');
        prev.delete('reason');
        return prev;
      },
      { replace: true }
    );
    // Intentionally run once on mount only — slackSearchOutcome is captured
    // via a useState initializer above and must not be re-derived from a
    // live searchParams dependency (see comment above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isConnected = status?.status === 'connected';

  const handleDisconnectConfirm = () => {
    disconnectBot.mutate(undefined, {
      onSuccess: () => setShowDisconnectConfirm(false),
    });
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    updateModel.mutate(value === '' ? null : value);
  };

  const handleTokenSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTokenError(null);
    const trimmed = tokenInput.trim();
    if (!trimmed) {
      setTokenError('Paste your Claude setup token to continue');
      return;
    }
    selfConnect.mutate(
      { claudeToken: trimmed },
      {
        onSuccess: (data: SelfConnectResult) => {
          setTokenInput('');
          if (data.tokenVerified === false) {
            setWarnMessage(
              "Token saved, but we couldn't verify it with Anthropic right now — Monk will tell you if it does not work."
            );
          } else {
            setWarnMessage(null);
          }
          if (isConnected) {
            setShowTokenForm(false);
          }
        },
        onError: (err: unknown) => {
          setTokenError(err instanceof Error ? err.message : 'Failed to save token');
        },
      }
    );
  };

  const handleCancelTokenForm = () => {
    setShowTokenForm(false);
    setTokenInput('');
    setTokenError(null);
  };

  const handleSlackSearchDisconnectConfirm = () => {
    disconnectSlackSearch.mutate(undefined, {
      onSuccess: (data) => {
        setShowSlackSearchDisconnectConfirm(false);
        // WR-12: the caveat is a genuine security warning ("Slack could not
        // be reached, remove access yourself") and must render ONLY for
        // 'revoke_failed' — never for 'nothing_to_revoke' (never connected,
        // or a second Disconnect click on an already-revoked grant), which
        // is not a failure and must not scare a monk into hunting for a
        // grant that is already gone.
        setSlackSearchRevokeCaveat(data.outcome === 'revoke_failed');
      },
    });
  };

  const handleConnectSlackSearch = () => {
    // Deliberate full-page redirect, never a fetch (mirrors
    // authContext.tsx:223's login() idiom). A fetch would follow the
    // backend's 302 and land Slack's OAuth consent page in a response body
    // instead of the browser — see useSlackSearch.ts's header comment.
    window.location.href = slackSearchConnectUrl();
  };

  const tokenForm = (mode: 'connect' | 'replace') => (
    <form onSubmit={handleTokenSubmit} className="space-y-3">
      <div>
        <label
          htmlFor="claude-setup-token"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Claude setup token
        </label>
        <input
          id="claude-setup-token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="sk-ant-oat..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {(tokenError || selfConnect.isError) && (
        <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {tokenError || 'Failed to save token'}
        </div>
      )}
      <div className="flex gap-3">
        <Button type="submit" isLoading={selfConnect.isPending}>
          {mode === 'connect' ? 'Connect' : 'Replace token'}
        </Button>
        {mode === 'replace' && (
          <Button type="button" variant="secondary" onClick={handleCancelTokenForm}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <ChatBubbleLeftRightIcon className="h-6 w-6 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Monk (Slack bot)</h2>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : isConnected ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              Connected
            </span>
            {status?.connectedAt && (
              <span className="text-gray-500">
                since {formatDistanceToNow(new Date(status.connectedAt), { addSuffix: true })}
              </span>
            )}
            {status?.patStatus && <PatStatusBadge status={status.patStatus} />}
          </div>

          <div>
            <label htmlFor="monka-model" className="block text-sm font-medium text-gray-700 mb-1">
              Monka model
            </label>
            <select
              id="monka-model"
              value={status?.model ?? ''}
              onChange={handleModelChange}
              disabled={updateModel.isPending}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {MODEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Changes take effect the next time your bot session restarts.
            </p>
            {updateModel.isError && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                Couldn&apos;t update model
              </p>
            )}
          </div>

          {warnMessage && (
            <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">{warnMessage}</div>
          )}

          {showTokenForm ? (
            tokenForm('replace')
          ) : (
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setShowTokenForm(true)}>
                Replace token
              </Button>
              <Button variant="danger" onClick={() => setShowDisconnectConfirm(true)}>
                Disconnect
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            Not connected — message Monk in Slack to get started.
          </p>
          <p className="text-xs text-gray-500">
            Already messaged Monk? Pasting a token here works once your Slack identity is linked.
          </p>
          {warnMessage && (
            <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">{warnMessage}</div>
          )}
          {tokenForm('connect')}
        </div>
      )}

      <ConfirmModal
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onConfirm={handleDisconnectConfirm}
        title="Disconnect Monk"
        message="Disconnecting revokes your Monk API token, deletes your stored Claude token, and revokes Monka's Slack search access if you connected it. To reconnect, you'll need a fresh connect link from Monk in Slack and a new Claude setup token."
        confirmText="Disconnect"
        variant="danger"
        isLoading={disconnectBot.isPending}
        error={disconnectBot.isError ? 'Failed to disconnect' : null}
      />

      {/*
        Phase 109 (Plan 109-08 / SLACK-SEARCH-03, D-10) — Slack search
        block, a second status area beneath the Claude-token controls in the
        same card (D-06 scopes this to a connect/disconnect control, not a
        new page). Phase 111 (D-111-01, D-111-10) widened the coverage to
        DMs and group DMs and added the connected-but-narrow reconnect
        notice below.
      */}
      <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Slack search</h3>

        {slackSearchOutcome === 'connected' && (
          <div role="status" className="p-3 bg-green-50 text-green-800 rounded-lg text-sm">
            {SLACK_SEARCH_OUTCOME_COPY.connected}
          </div>
        )}
        {slackSearchOutcome === 'declined' && (
          <div role="status" className="p-3 bg-gray-50 text-gray-700 rounded-lg text-sm">
            {SLACK_SEARCH_OUTCOME_COPY.declined}
          </div>
        )}
        {slackSearchOutcome === 'session-expired' && (
          <div role="status" className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
            {SLACK_SEARCH_OUTCOME_COPY['session-expired']}
          </div>
        )}
        {slackSearchOutcome === 'error' && (
          <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {resolveSlackSearchErrorReason(slackSearchErrorReason)}
          </div>
        )}

        {isSlackSearchLoading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : slackSearchStatus?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                Connected
              </span>
              {slackSearchStatus.connectedAt && (
                <span className="text-gray-500">
                  since{' '}
                  {formatDistanceToNow(new Date(slackSearchStatus.connectedAt), {
                    addSuffix: true,
                  })}
                </span>
              )}
            </div>

            {slackSearchStatus.scopes.length > 0 && (
              <p className="text-xs text-gray-500">
                Granted scopes: {slackSearchStatus.scopes.join(', ')}
              </p>
            )}

            {slackSearchRevokeCaveat && (
              <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                The stored credential was deleted, but Slack could not be reached to revoke the
                grant. You can remove Monka&apos;s access yourself from your Slack account&apos;s
                app settings.
              </div>
            )}

            {!hasAllRequiredScopesForReconnectCheck(slackSearchStatus.scopes) && (
              <>
                <div role="status" className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                  Monka now searches your DMs and group DMs too. This connection was granted
                  before that, so it does not cover them yet. Reconnect to update it — until you
                  do, Slack search will refuse rather than quietly search less than it says.
                </div>
                <Button variant="secondary" onClick={handleConnectSlackSearch}>
                  Reconnect Slack search
                </Button>
              </>
            )}

            <Button
              variant="danger"
              onClick={() => setShowSlackSearchDisconnectConfirm(true)}
            >
              Disconnect Slack search
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Monka can search the public and private channels you can already see, plus your DMs
              and group DMs. It searches only with your own Slack access, so it can never see
              anything you cannot.
            </p>

            {slackSearchStatus?.revokedAt && (
              <p className="text-xs text-gray-500">
                Access was last revoked{' '}
                {formatDistanceToNow(new Date(slackSearchStatus.revokedAt), {
                  addSuffix: true,
                })}
                .
              </p>
            )}

            {slackSearchRevokeCaveat && (
              <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                The stored credential was deleted, but Slack could not be reached to revoke the
                grant. You can remove Monka&apos;s access yourself from your Slack account&apos;s
                app settings.
              </div>
            )}

            <Button variant="secondary" onClick={handleConnectSlackSearch}>
              Connect Slack search
            </Button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showSlackSearchDisconnectConfirm}
        onClose={() => setShowSlackSearchDisconnectConfirm(false)}
        onConfirm={handleSlackSearchDisconnectConfirm}
        title="Disconnect Slack search"
        message="Disconnecting revokes Monka's access at Slack and deletes the stored credential. Monka will no longer be able to search your Slack history."
        confirmText="Disconnect"
        variant="danger"
        isLoading={disconnectSlackSearch.isPending}
        error={disconnectSlackSearch.isError ? "Couldn't disconnect Slack search" : null}
      />
    </div>
  );
}
